import { useState } from 'react';

import { Trans } from '@lingui/react/macro';
import { useUser } from '@clerk/react-router';

import { motion } from 'framer-motion';
import { ArrowRight, Clock, Sparkles, CreditCard } from 'lucide-react';

import CreditPurchaseModal from '~/components/CreditPurchaseModal';
import TryForFreeButton from '~/components/TryForFreeButton';

interface PlanDot {
  color: 'dark' | 'gold' | 'transparent';
}

interface Plan {
  name: string;
  price: string;
  credits: string;
  highlighted: boolean;
  grid: { cols: number; dots: PlanDot[] };
}

const plans: Plan[] = [
  {
    name: 'Starter',
    price: '$29',
    credits: '2 Practice Credits',
    highlighted: false,
    grid: {
      cols: 1,
      dots: [
        { color: 'dark' },
        { color: 'dark' },
      ],
    },
  },
  {
    name: 'Pro',
    price: '$59',
    credits: '4 Practice Credits + 2 Free',
    highlighted: true,
    grid: {
      cols: 3,
      dots: [
        { color: 'dark' }, { color: 'dark' }, { color: 'gold' },
        { color: 'dark' }, { color: 'dark' }, { color: 'gold' },
      ],
    },
  },
  {
    name: 'Advanced',
    price: '$99',
    credits: '8 Practice Credits + 4 Free',
    highlighted: false,
    grid: {
      cols: 6,
      dots: [
        { color: 'dark' }, { color: 'dark' },{ color: 'dark' },{ color: 'dark' }, { color: 'gold' }, { color: 'gold' },
        { color: 'dark' }, { color: 'dark' },{ color: 'dark' },{ color: 'dark' }, { color: 'gold' }, { color: 'gold' },
      ],
    },
  },
];

function DotGrid({ grid, highlighted }: { grid: Plan['grid']; highlighted: boolean }) {
  const rows = Math.ceil(grid.dots.length / grid.cols);
  return (
    <div className="flex flex-col items-center gap-1.5 h-16 justify-center">
      {Array.from({ length: rows }).map((_, row) => (
        <div key={row} className="flex gap-1.5">
          {Array.from({ length: grid.cols }).map((_, col) => {
            const idx = row * grid.cols + col;
            if (idx >= grid.dots.length) return null;
            const dot = grid.dots[idx];
            let colorClass: string;
            if (dot.color === 'transparent') {
              colorClass = 'bg-transparent';
            } else if (highlighted) {
              colorClass = dot.color === 'gold' ? 'bg-brand-gold' : 'bg-white';
            } else {
              colorClass = dot.color === 'gold' ? 'bg-brand-gold' : 'bg-brand-dark-blue';
            }
            return (
              <div
                key={col}
                className={`w-2.5 h-2.5 rounded-full ${colorClass}`}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}

export default function PricingSection() {
  const [modalOpen, setModalOpen] = useState(false);
  const { isSignedIn } = useUser();

  const handleGetCredits = () => {
    if (isSignedIn) {
      setModalOpen(true);
    } else {
      window.location.href = '/sign-up';
    }
  };

  return (
    <section id="pricing" className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-[1080px] mx-auto w-full px-4">
        {/* Header */}
        <motion.div
          className="text-center mb-8 sm:mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-gold">
            <Trans>Pricing</Trans>{' '}
            <span className="inline-block">✨</span>
          </h2>
        </motion.div>

        {/* Trust badges */}
        <motion.div
          className="flex flex-wrap items-center justify-center gap-3 sm:gap-6 mb-10 bg-gray-50 rounded-full px-6 py-3 max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Clock className="w-4 h-4 text-gray-400" />
            <Trans>30-Day Money Back</Trans>
          </div>
          <div className="hidden sm:block w-px h-4 bg-gray-300" />
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Sparkles className="w-4 h-4 text-gray-400" />
            <Trans>Credits Never Expire</Trans>
          </div>
          <div className="hidden sm:block w-px h-4 bg-gray-300" />
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <CreditCard className="w-4 h-4 text-gray-400" />
            <Trans>1 Credit = 1 Practice</Trans>
            <ArrowRight className="w-3 h-3 text-gray-400" />
          </div>
        </motion.div>

        {/* Pricing cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl mx-auto mb-10 items-stretch">
          {plans.map((plan, i) => (
            <motion.div
              key={plan.name}
              className={`rounded-2xl p-6 sm:p-8 text-center flex flex-col items-center ${
                plan.highlighted
                  ? 'bg-brand-dark-blue text-white shadow-xl sm:scale-105'
                  : 'bg-white border border-gray-200 text-gray-900 shadow-sm'
              }`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
            >
              <DotGrid grid={plan.grid} highlighted={plan.highlighted} />
              <h3 className={`text-lg font-bold mt-3 mb-4 ${plan.highlighted ? 'text-brand-gold' : 'text-brand-dark-blue'}`}>
                {plan.name}
              </h3>
              <p className={`text-4xl font-bold mb-2 ${plan.highlighted ? 'text-white' : 'text-gray-900'}`}>
                {plan.price}
              </p>
              <p className={`text-sm mb-6 ${plan.highlighted ? 'text-white/80' : 'text-gray-500'}`}>
                {plan.credits}
              </p>
              <button
                onClick={handleGetCredits}
                className={`w-full rounded-full py-3 px-6 font-semibold text-sm flex items-center justify-center gap-2 transition-all mt-auto ${
                  plan.highlighted
                    ? 'bg-white text-brand-dark-blue hover:bg-gray-100'
                    : 'bg-brand-dark-blue text-white hover:bg-brand-dark-blue/90'
                }`}
              >
                <Trans>Get Credits</Trans>
                <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          ))}
        </div>

        {/* Payment methods */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <p className="text-xs text-gray-400 uppercase tracking-wide mb-3">
            <Trans>Accepted Payment Methods</Trans>
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 mb-8">
            {['Visa', 'Mastercard', 'Amex', 'Apple Pay', 'Google Pay'].map((method) => (
              <div
                key={method}
                className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 text-xs text-gray-600"
              >
                <CreditCard className="w-3 h-3 text-gray-400" />
                {method}
              </div>
            ))}
          </div>
          <TryForFreeButton size="lg" />
        </motion.div>
      </div>

      {/* Credit Purchase Modal */}
      <CreditPurchaseModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        returnUrl={typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/pricing'}
      />
    </section>
  );
}
